
import api from '../../../utils/api';
import AddRegionForm from '../AddRegionForm';
import AddVegForm from '../AddVegForm';
import MapGpsPicker from '../../../components/MapGpsPicker';
import SuggestionList, { regionRow, speciesRow } from '../SuggestionList';
import { SUBSTRATES } from '../fieldOptions';
import { btnStyle, gridStyle, hintStyle, inputStyle, labelStyle, stepDescStyle } from '../styles';

/**
 * Where and when it was found, and what it was growing in and among.
 *
 * The step that has to be filled in while standing over the specimen, because
 * none of it can be recovered afterwards: the coordinates, the substrate, the
 * plants around it. It is also the step that writes into two dictionaries --
 * regions and vegetation species -- so both search boxes can offer to add
 * what they could not find, through AddRegionForm and AddVegForm.
 *
 * The region box does something worth naming. Losing focus without having
 * chosen anything puts back the last region that WAS chosen, rather than
 * leaving half-typed text that means nothing. A region is either one the
 * dictionary knows or it is not a region.
 *
 * `regionField` and `vegField` each bundle a search box with what the wizard
 * keeps around it: whether a choice was made, the counter that lets a stale
 * lookup be ignored, and whether the add-to-dictionary form is open.
 */
export default function Step2({
  t, canEdit, form, onFormChange, setForm, regionField, vegField,
  supplementary, setSupplementary, supplementaryInput, setSupplementaryInput,
}) {
  // The half-typed entry belongs to the wizard, not to this step: moving on to
  // step 3 unmounts this component, and somebody who steps back should find
  // what they were in the middle of typing still there.
  return (
  <div>
    <p style={stepDescStyle}>{t('step2.desc')}</p>

    <label style={{ ...labelStyle, marginBottom: 14, maxWidth: 260 }}>
      {t('step2.dateTimeLabel')}
      <input name="collection_datetime" type="datetime-local" value={form.collection_datetime} onChange={onFormChange} style={inputStyle} title={t('tip.dateTime')} />
    </label>

    {/* GPS */}
    <MapGpsPicker
      lat={form.gps_lat}
      lng={form.gps_lng}
      hintText={t('step2.mapHint')}
      onChange={(lat, lng) => setForm(f => ({ ...f, gps_lat: lat, gps_lng: lng }))}
      onPinDropped={(lat, lng) => {
        const seq = ++regionField.seqRef.current;
        api.get(`/regions/at?lat=${lat}&lng=${lng}`)
          .then(res => {
            if (seq !== regionField.seqRef.current) return; // superseded by a later click, manual typing, or manual selection
            if (res.data) regionField.onPick(res.data);
          })
          .catch(() => {
            if (seq !== regionField.seqRef.current) return;
            regionField.box.fail();
          });
      }}
    />
    {form.gps_lat && form.region && (
      <p style={{ fontSize: 12, color: 'var(--c-success-fg)', margin: '-8px 0 10px' }}>
        {t('step2.pinRegionSuffix', { region: t(form.region, { ns: 'regions' }) })}
      </p>
    )}

    {/* Region + Substrate */}
    <div style={{ ...gridStyle, marginBottom: 16 }}>
      <label style={labelStyle}>
        {t('step2.region')}
        <div style={{ position: 'relative' }}>
          <input
            data-testid="wizard-region"
            title={t('tip.region')}
            value={regionField.box.query}
            onChange={regionField.onInput}
            onFocus={() => { if (regionField.box.suggestions.length > 0) regionField.box.show(); }}
            onBlur={() => setTimeout(() => {
              regionField.box.close();
              if (!regionField.selected) {
                regionField.box.reset(regionField.lastConfirmedRef.current.query);
                setForm(f => ({ ...f, region: regionField.lastConfirmedRef.current.value }));
                regionField.setSelected(!!regionField.lastConfirmedRef.current.value);
              }
            }, 150)}
            placeholder={t('step2.regionPlaceholder')}
            style={{ ...inputStyle, fontStyle: regionField.selected ? 'italic' : 'normal' }}
            autoComplete="off"
            dir="rtl"
          />
          <SuggestionList box={regionField.box} testId="wizard-region-suggestion"
            onPick={regionField.onPick}>{regionRow}</SuggestionList>
        </div>
        {regionField.box.noMatch && regionField.box.query.length >= 2 && (
          <span style={{ display: 'block', marginTop: 5, fontSize: 12, color: 'var(--c-danger-fg)' }}>
            {t('step2.regionNoMatch')}
          </span>
        )}
        {canEdit && regionField.box.noMatch && regionField.box.query.length >= 2 && !regionField.adding && (
          <button type="button" onClick={() => regionField.setAdding(true)} onMouseDown={e => e.preventDefault()} title={t('tip.addRegion')}
            style={{ display: 'block', marginTop: 5, fontSize: 12, color: 'var(--c-primary-fg)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
            {t('step2.addRegionPrompt', { query: regionField.box.query })}
          </button>
        )}
        {regionField.adding && (
          <AddRegionForm t={t} initialQuery={regionField.box.query} seqRef={regionField.seqRef}
            onPicked={regionField.onPick} onClose={() => regionField.setAdding(false)} />
        )}
        {regionField.box.failed && (
          <span style={{ display: 'block', marginTop: 5, fontSize: 12, color: 'var(--c-danger-fg)' }}>
            {t('step2.regionSearchError')}
          </span>
        )}
      </label>
      <label style={labelStyle}>
        {t('step2.substrateLabel')}
        <select name="substrate" data-testid="wizard-substrate" title={t('tip.substrate')}
                value={form.substrate} onChange={onFormChange} style={inputStyle}>
          <option value="">{t('common:selectPlaceholder')}</option>
          {SUBSTRATES.map(s => <option key={s} value={s}>{t(`substrate.${s}`)}</option>)}
        </select>
      </label>
      <label style={labelStyle}>
        {t('step2.countryCode')}
        <input name="country_code" title={t('tip.countryCode')} value={form.country_code} onChange={onFormChange}
               maxLength={2} placeholder="IL" style={{ ...inputStyle, textTransform: 'uppercase' }} />
        <span style={hintStyle}>{t('step2.countryCodeHint')}</span>
      </label>
      <label style={labelStyle}>
        {t('step2.localityText')}
        <input name="locality_text" title={t('tip.locality')} value={form.locality_text} onChange={onFormChange}
               style={inputStyle} />
        <span style={hintStyle}>{t('step2.localityTextHint')}</span>
      </label>
    </div>

    {/* Habitat / Woody vegetation */}
    <div style={{ borderTop: '1px solid var(--c-border)', paddingTop: 16, marginTop: 4 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-text-fg)', marginBottom: 12 }}>{t('step2.habitat')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 14 }}>
        <label style={labelStyle}>
          {t('step2.hostSpecies')}
          <input name="host_species" title={t('tip.hostSpecies')} value={form.host_species} onChange={onFormChange} style={inputStyle} />
          <span style={hintStyle}>{t('step2.hostSpeciesHint')}</span>
        </label>
        <label style={labelStyle}>
          {t('step2.microenvironment')}
          <input name="microenvironment" title={t('tip.microenvironment')} value={form.microenvironment} onChange={onFormChange} style={inputStyle} />
          <span style={hintStyle}>{t('step2.microenvironmentHint')}</span>
        </label>
        <label style={labelStyle}>
          {t('step2.habitatBiome')}
          <input name="habitat_biome" title={t('tip.habitatBiome')} value={form.habitat_biome} onChange={onFormChange} style={inputStyle} />
          <span style={hintStyle}>{t('step2.habitatBiomeHint')}</span>
        </label>
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
        {t('step2.woodyVegLabel')}
        <span style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', fontWeight: 400, marginInlineStart: 6 }}>{t('step2.woodyVegHint')}</span>
      </div>
      {vegField.list.some(v => v.species === 'unknown') && (
        <div style={{ background: 'var(--c-warning-soft-2)', border: '1px solid var(--c-warning)', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: 'var(--c-warning-text-fg)', marginBottom: 8 }}>
          {t('step2.unknownVegWarning')}
        </div>
      )}
      <div style={{ position: 'relative', marginBottom: 8 }}>
        <input
          data-testid="wizard-veg"
          title={t('tip.vegSearch')}
          value={vegField.box.query}
          onChange={vegField.onInput}
          onFocus={() => { if (vegField.box.suggestions.length > 0) vegField.box.show(); }}
          onBlur={() => setTimeout(() => vegField.box.close(), 150)}
          placeholder={t('step2.vegSearchPlaceholder')}
          style={{ ...inputStyle, width: '100%' }}
          autoComplete="off"
          dir="ltr"
        />
        <SuggestionList box={vegField.box} testId="wizard-veg-suggestion"
          onPick={vegField.onPick}>{speciesRow}</SuggestionList>
      </div>
      {vegField.box.failed && (
        <span style={{ display: 'block', marginBottom: 8, fontSize: 12, color: 'var(--c-danger-fg)' }}>
          {t('step2.vegSearchError')}
        </span>
      )}
      {vegField.box.query.trim().length >= 2 && (
        <div style={{ marginBottom: 8 }}>
          {vegField.box.noMatch && (
            <span style={{ display: 'block', fontSize: 12, color: 'var(--c-danger-fg)', marginBottom: 4 }}>
              {t('step2.vegNoMatch')}
            </span>
          )}
          <button type="button" onClick={vegField.onFreeText} data-testid="wizard-veg-free-text" title={t('tip.vegFreeText')}
            onMouseDown={e => e.preventDefault()}
            style={{ ...btnStyle('var(--c-text-muted)'), fontSize: 12, padding: '4px 10px', marginInlineEnd: 8 }}>
            {t('step2.addVegFreeText', { query: vegField.box.query.trim() })}
          </button>
          {vegField.box.noMatch && canEdit && !vegField.adding && (
            <button type="button" onClick={() => vegField.setAdding(true)} onMouseDown={e => e.preventDefault()} title={t('tip.addNewSpecies')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary-fg)', fontSize: 12, padding: 0, textDecoration: 'underline' }}>
              {t('step2.addVegPrompt', { query: vegField.box.query })}
            </button>
          )}
        </div>
      )}
      {vegField.adding && (
        <AddVegForm t={t} initialQuery={vegField.box.query} seqRef={vegField.seqRef}
          onPicked={vegField.onPick} onClose={() => vegField.setAdding(false)} />
      )}
      <button type="button" onClick={vegField.onUnknown} title={t('tip.vegUnknown')}
        style={{ ...btnStyle('var(--c-text-muted)'), fontSize: 12, padding: '4px 10px', marginBottom: 10 }}>
        {t('step2.markUnknownBtn')}
      </button>
      {vegField.list.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {vegField.list.map((v, i) => (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: v.is_dominant ? 'var(--c-success-soft)' : 'var(--c-primary-soft-3)',
              border: `1px solid ${v.is_dominant ? 'var(--c-primary-border-3)' : 'var(--c-border-strong-3)'}`,
              borderRadius: 14, padding: '3px 10px', fontSize: 12,
            }}>
              <span style={{ fontStyle: v.species !== 'unknown' ? 'italic' : 'normal' }} dir={v.species !== 'unknown' ? 'ltr' : undefined}>
                {v.species === 'unknown' ? t('vegSpecial.unknown') : v.species}
              </span>
              {v.hebrew_name && (
                <span style={{ color: 'var(--c-text-muted-fg)' }} dir="rtl">({v.hebrew_name})</span>
              )}
              <button type="button" onClick={() => vegField.onToggleDominant(i)} title={t('tip.toggleDominant')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: v.is_dominant ? 'var(--c-success-fg)' : 'var(--c-text-faint-fg)', fontSize: 13, padding: 0, lineHeight: 1 }}>
                ★
              </button>
              <button type="button" onClick={() => vegField.onRemove(i)} title={t('tip.removeItem')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-muted-fg)', fontSize: 14, padding: 0, lineHeight: 1 }}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
          {t('step2.supplementaryLabel')}
          <span style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', fontWeight: 400, marginInlineStart: 6 }}>{t('step2.supplementaryHint')}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input
            value={supplementaryInput}
            onChange={e => setSupplementaryInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const v = supplementaryInput.trim();
                if (v) { setSupplementary(list => [...list, v]); setSupplementaryInput(''); }
              }
            }}
            placeholder={t('step2.supplementaryPlaceholder')}
            title={t('tip.suppInput')}
            style={{ ...inputStyle, flex: 1 }}
          />
          <button type="button" onClick={() => {
            const v = supplementaryInput.trim();
            if (v) { setSupplementary(list => [...list, v]); setSupplementaryInput(''); }
          }} title={t('tip.suppAdd')} style={{ ...btnStyle('var(--c-primary)'), padding: '0 16px' }}>
            {t('common:add')}
          </button>
        </div>
        {supplementary.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {supplementary.map((item, i) => (
              <span key={i} style={{
                display: 'flex', alignItems: 'center', gap: 6, background: 'var(--c-primary-soft-3)',
                border: '1px solid var(--c-border-strong-3)', borderRadius: 14, padding: '3px 10px', fontSize: 12,
              }}>
                {item}
                <button type="button" onClick={() => setSupplementary(list => list.filter((_, idx) => idx !== i))} title={t('tip.removeItem')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-muted-fg)', fontSize: 14, padding: 0, lineHeight: 1 }}>
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
  );
}
